import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { smartCardsConfig, placeSmartCards, markCardSeen, dismissCard, reportSmartCardEvent } from "@/lib/smartCards";
import { whenFeedReady } from "@/lib/lowPriority";
import { api } from "@/lib/api";
import { BirthdayWidget, useBirthdays } from "@/components/BirthdayWidget";
import { FeaturedTestimony, useFeaturedTestimony } from "@/components/FeaturedTestimony";
import { LiveNowWidget, useLiveNow } from "@/components/LiveNowWidget";
import { PrayerChainBanner, usePrayerChain } from "@/components/PrayerChainBanner";
import { PrayersSpotlightWidget, useSpotlight } from "@/components/PrayersSpotlightWidget";
import { ProfileLiveCard } from "@/components/ProfileLiveCard";
import { PromoOfTheDayCard, usePromoOfTheDay } from "@/components/PromoOfTheDayCard";
import { MarketForYouCard, useMarketForYou } from "@/components/marketplace/MarketFeedCards"; // lot 791
import { useGuard } from "@/lib/guards";

const today = () => new Date().toISOString().slice(0, 10);
const SETTLE_TIMEOUT_MS = 4000;

// Décisions de la session (module) : une carte placée garde sa place jusqu'au prochain démarrage de l'app
// (retour sur le fil compris) ; nouveau contenu ou « × » → décision refaite. Remises à zéro si le compte change.
const session = { uid: null, decided: {} };
const sessionDecisions = (uid) => {
  if (session.uid !== uid) { session.uid = uid; session.decided = {}; }
  return session.decided;
};
export const resetSmartSession = () => { session.uid = null; session.decided = {}; hiddenFollowedLives.clear(); };

// Lot 669 — « × » sur la carte « membre suivi en direct » : masquée pour la session (par direct).
const hiddenFollowedLives = new Set();

// Cadre d'une carte intelligente : marque la carte « vue » quand 60 % de sa surface a été affichée ; un tap dans la
// carte (hors « × ») compte comme « ouverte » pour les statistiques admin.
const SmartCardFrame = ({ cardKey, contentKey, position, track, className = "", children }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !contentKey) return undefined;
    const seen = () => { markCardSeen(cardKey, contentKey); if (track) reportSmartCardEvent(cardKey, "seen", contentKey, api.post); };
    if (typeof IntersectionObserver === "undefined") { seen(); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { seen(); io.disconnect(); }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, [cardKey, contentKey, track]);
  const onClickCapture = (e) => {
    if (!track || e.target.closest("[data-testid$='-dismiss']")) return;
    reportSmartCardEvent(cardKey, "opened", contentKey, api.post);
  };
  return (
    <div ref={ref} className={className} onClickCapture={onClickCapture} data-testid={`smart-card-${cardKey}`} data-content-key={contentKey} data-position={position}>
      {children}
    </div>
  );
};

// Charge les 5 sources une seule fois, calcule la carte de tête + les cartes glissées dans le fil.
// Retour : { enabled, head: node|null, slotAt(nbPostsBefore) → node[]|null, tail(nbPosts) → node[]|null }.
export const useSmartFeedCards = () => {
  const { user, config } = useApp();
  const settings = config?.settings;
  const feats = settings?.features || {};
  const cfg = useMemo(() => smartCardsConfig(settings), [settings]);
  const on = cfg.enabled;
  const uid = user?.id || "guest";

  const onTestimony = on && feats.testimonies !== false;
  const onChain = on && feats.prayers !== false && feats.prayer_chain !== false;
  const onSpotlight = on && feats.prayers !== false;
  const birthdays = useBirthdays(user, on);
  const testimony = useFeaturedTestimony(onTestimony);
  // Lot 669 — toujours chargé (cartes intelligentes ou non) : une seule source pour la carte « membre suivi en direct »
  // (tête du fil, hors rotation/cooldown) et pour le widget « En direct » (qui ne répète pas ce direct).
  const liveAll = useLiveNow(true);
  const [hiddenVersion, setHiddenVersion] = useState(0);
  const liveCardsOn = useGuard("live_preview_cards"); // lot 671 — garde-fou admin : coupé → le direct reste dans le widget
  const followedLive = useMemo(
    () => (liveCardsOn ? liveAll.lives.find((l) => l.mine === "host" && !hiddenFollowedLives.has(l.id)) || null : null),
    [liveAll.lives, hiddenVersion, liveCardsOn],  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const live = useMemo(
    () => (followedLive ? { ...liveAll, lives: liveAll.lives.filter((l) => l.id !== followedLive.id) } : liveAll),
    [liveAll, followedLive],
  );
  const hideFollowed = (id) => { hiddenFollowedLives.add(id); setHiddenVersion((n) => n + 1); };
  const chain = usePrayerChain(onChain);
  const spotlight = useSpotlight(onSpotlight);
  const onPromo = on && feats.marketplace !== false; // lot 695 — « Promo du jour » suit le réglage Marketplace
  const promo = usePromoOfTheDay(onPromo);
  // Lot 791 — « Marketplace pour toi » (membre connecté) : une annonce mise en avant, choisie d'après ses goûts ; garde-fou + réglage Marketplace.
  const forYouGuard = useGuard("market_feed_card");
  const onForYou = onPromo && forYouGuard && !!user;
  const forYou = useMarketForYou(onForYou);

  // Priorité stricte : la décision initiale attend que toutes les sources aient répondu (ou fil prêt + 4 s max),
  // sinon la 1re source arrivée prendrait la tête à la place d'un Live ou d'un anniversaire.
  const settled = birthdays.loaded && !live.loading
    && (!onTestimony || testimony.loaded) && (!onChain || chain.items !== null) && (!onSpotlight || spotlight.data !== null)
    && (!onPromo || promo.loaded) && (!onForYou || forYou.loaded);
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!on || settled) return undefined;
    let t = null;
    const cancel = whenFeedReady(() => { t = setTimeout(() => setTimedOut(true), SETTLE_TIMEOUT_MS); });
    return () => { cancel(); if (t) clearTimeout(t); };
  }, [on, settled]);
  const ready = settled || timedOut;

  const available = useMemo(() => ({
    live_now: live.lives.length ? `live:${live.lives.map((l) => l.id).sort().join(",")}` : null,
    birthday: birthdays.loaded
      ? (birthdays.birthdays.length ? `bday:${today()}:${birthdays.birthdays.map((b) => b.id).sort().join(",")}` : (birthdays.hasUpcoming ? `bday-upcoming:${today()}` : null))
      : null,
    prayer_chain: onChain && chain.items?.length ? `chain:${chain.items[0].id}` : null,
    testimony: onTestimony && testimony.post ? `testimony:${testimony.post.id}` : null,
    prayers_spotlight: onSpotlight && (Number(spotlight.data?.waiting_count) || 0) > 0
      ? `spot:${(spotlight.data.items || []).slice(0, 3).map((p) => p.id).join(",") || today()}` : null,
    // Nouvelle annonce remisée ou remise modifiée → nouvelle clé → la carte revient malgré le délai.
    promo_day: onPromo && promo.listing?.promo ? `promo:${promo.listing.id}:${promo.listing.promo.percent}` : null,
    // Lot 791 — nouvelle annonce proposée (id différent) → nouvelle clé → la carte revient malgré le délai.
    market_for_you: onForYou && forYou.listings[0] ? `mfy:${forYou.listings[0].id}` : null,
  }), [live.lives, birthdays.loaded, birthdays.birthdays, birthdays.hasUpcoming, chain.items, testimony.post, spotlight.data, promo.listing, onChain, onTestimony, onSpotlight, onPromo, onForYou, forYou.listings]);

  const [version, setVersion] = useState(0);
  const placement = useMemo(() => {
    if (!on || !ready) return {};
    const next = placeSmartCards(available, cfg, sessionDecisions(uid));
    session.decided = next;
    return next;
  }, [on, ready, available, cfg, uid, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const followedNode = followedLive
    ? <ProfileLiveCard key={`followed-live-${followedLive.id}`} live={followedLive} host={followedLive.host} poll={false} onDismiss={() => hideFollowed(followedLive.id)} testId="feed-followed-live" />
    : null;
  if (!on) return { enabled: false, head: null, slotAt: () => null, tail: () => null, live, followedLive: followedNode };

  const dismiss = (k) => {
    dismissCard(k, available[k]);
    if (user) reportSmartCardEvent(k, "dismissed", available[k], api.post);
    delete session.decided[k];
    setVersion((n) => n + 1);
  };
  const render = (k) => {
    const ck = available[k];
    const node = {
      live_now: <LiveNowWidget data={live} onDismiss={() => dismiss(k)} />,
      birthday: <BirthdayWidget hideWhenEmpty data={birthdays} onDismiss={() => dismiss(k)} />,
      prayer_chain: <PrayerChainBanner data={chain} onDismiss={() => dismiss(k)} />,
      testimony: <FeaturedTestimony data={testimony.post} onDismiss={() => dismiss(k)} />,
      prayers_spotlight: <PrayersSpotlightWidget variant="compact" data={spotlight} onDismiss={() => dismiss(k)} />,
      promo_day: <PromoOfTheDayCard data={promo} onDismiss={() => dismiss(k)} />,
      market_for_you: <MarketForYouCard data={forYou} onDismiss={() => dismiss(k)} />,
    }[k];
    // Anniversaires et Prières à la une restent dans la colonne de droite sur PC (comme avant).
    const cls = (k === "birthday" || k === "prayers_spotlight") ? "lg:hidden" : "";
    return <SmartCardFrame key={`smart-${k}`} cardKey={k} contentKey={ck} position={placement[k].at} track={!!user} className={cls}>{node}</SmartCardFrame>;
  };
  const headKey = cfg.order.find((k) => placement[k]?.at === "head");
  const keysAt = (pred) => cfg.order.filter((k) => placement[k] && placement[k].at !== "head" && pred(placement[k].at));
  return {
    enabled: true,
    live,
    followedLive: followedNode,
    head: headKey ? render(headKey) : null,
    slotAt: (nbPostsBefore) => { const keys = keysAt((at) => at === nbPostsBefore); return keys.length ? keys.map(render) : null; },
    // Fil court : les cartes prévues plus loin que le dernier post sont ajoutées à la fin.
    tail: (nbPosts) => { const keys = keysAt((at) => at > nbPosts); return keys.length ? keys.map(render) : null; },
  };
};
